import { LANGUAGES } from "./../../../helpers/constant";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import cloneDeep from "lodash/cloneDeep";

import { AccountApi } from "../../../api/AccountApi";
import { RootState } from "../../../store/store";
import { UserType } from "../types/types";
import {
  axiosAccountPrivate,
  axiosAccountPrivateDomainV2,
  axiosAccountPrivateQticketModuleV2,
  axiosAccountPrivateV2,
  axiosReportPrivate
} from "../../../api/axios";
import {
  RequestApiPrivateGticket,
  RequestApiPrivateGTicketV2
} from "../../gticket/api/axios";
import { DEFAULT_LANGUAGE } from "../../../helpers/constant";
import { renderServerErrorMsg } from "../../../helpers/errors";
import { updateI18nLocale } from "../../../helpers/i18n";
import {
  RequestApiPrivateQuickTicket,
  RequestApiPrivateQuickTicketAdmin,
  RequestApiPrivateUsers,
  RequestApiPrivateUsersV2
} from "../../system-management/api/axios";
import { generateGprocessToken } from "../../g-process/helpers/token";
import { generateGPerformanceToken } from "../../g-performance/api/auth";
import {
  generateReportToken,
  getReportMe
} from "../../magic-mirror/api/reportAuthApi";
import { ReportRole } from "../../magic-mirror/types";

type AuthState = {
  authUser: UserType | null;
  accessToken: string | null;
  expiresAt: number | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  updateLocaleStatus: "idle" | "loading" | "succeeded" | "failed";
  kpiUser: { role_actions: any };
  tokenGTicket: string | null;
  tokenGPerformance: string | null;
  isUserInactive: boolean;
  /** Vai trò report-GT (Gương Thần) — `manager`/`staff`, lấy LIVE từ §1.1 GET /reports/me. */
  reportRole: ReportRole | null;
  reportRoleStatus: "idle" | "loading" | "succeeded" | "failed";
};

/** Gắn token nội bộ Account service làm Authorization mặc định cho mọi instance account. */
const setAccountServiceToken = (token: string) => {
  const bearer = `Bearer ${token}`;
  axiosAccountPrivate.defaults.headers.common["Authorization"] = bearer;
  axiosAccountPrivateV2.defaults.headers.common["Authorization"] = bearer;
  axiosAccountPrivateQticketModuleV2.defaults.headers.common["Authorization"] =
    bearer;
  RequestApiPrivateUsersV2.defaults.headers.common["Authorization"] = bearer;
  axiosAccountPrivateDomainV2.defaults.headers.common["Authorization"] = bearer;
};

// Đổi SSO access token → hồ sơ nội bộ (`info-by-username`) rồi phát token riêng
// cho từng phân hệ (Account service, G-Ticket, G-Performance, G-Process). Tất cả
// các request này gửi `Authorization: Bearer <SSO access token>`
// (withSsoAuthorization); token nội bộ trả về mới được gắn làm Authorization mặc
// định trên các axios instance. Token account KHÔNG lấy từ `info-by-username`
// nữa mà từ `POST /api/auth/generate-token` của Account service.
const fetchUserInfo = createAsyncThunk(
  "auth/userInfo",
  async (payload: {
    email: string;
    phone: string;
    employeeid: string;
    username: string;
    callback?: any;
    dispatch: any;
    expiresAt: number;
    /** SSO access token (oidc `user.access_token`). */
    ssoAccessToken: string;
  }) => {
    const {
      email,
      phone,
      employeeid,
      username,
      callback,
      dispatch,
      expiresAt,
      ssoAccessToken
    } = payload;
    const payloadNew = {
      user_name: {
        email: email || undefined,
        code: employeeid || undefined,
        mobile: phone || undefined,
        username: username || undefined
      }
    };

    try {
      const { data } = await AccountApi.getUserInfo(
        payloadNew,
        ssoAccessToken
      );

      const tokenPromises: Promise<any>[] = [];
      // Account service: phát token riêng; nếu lỗi thì tạm dùng `data.token`
      // (nếu BE còn trả) để không chặn login.
      const accountTokenPromise: Promise<string> = AccountApi.generateToken(
        data?.email,
        ssoAccessToken
      )
        .then((res) => res?.data?.token ?? "")
        .catch((e) => {
          console.log("Account service generate token error:\n", e);
          return data?.token ?? "";
        })
        .then((accountToken: string) => {
          setAccountServiceToken(accountToken);
          return accountToken;
        });
      tokenPromises.push(accountTokenPromise);
      if (data?.user_name || data?.email) {
        tokenPromises.push(
          AccountApi.generateTokenGticket(
            data.user_name ?? data.email,
            ssoAccessToken
          )
            .then((gticketResponse) => {
              dispatch(
                setTokenGticket({ token: gticketResponse?.data?.token ?? "" })
              );
              return gticketResponse;
            })
            .catch((error) => {
              console.log("G-Ticket generate token error:\n", error);
              return { ...data };
            })
        );
      } else {
        console.error("************ Can't find username or email ************");
      }
      tokenPromises.push(
        generateGPerformanceToken(
          { id: data?.id, groups: data?.groups },
          expiresAt,
          ssoAccessToken
        )
          .then((resGenTokenGperformance) => {
            dispatch(
              setTokenGPerformance(resGenTokenGperformance?.data?.access_token)
            );
            return resGenTokenGperformance;
          })
          .catch((e) => {
            console.log("G-Performance generate token error:\n", e);
            return { ...data };
          })
      );
      tokenPromises.push(
        generateGprocessToken(data, expiresAt, ssoAccessToken).catch((e) => {
          console.log("G-Process generate token error:\n", e);
          return { ...data };
        })
      );
      await Promise.all(tokenPromises);
      const token = await accountTokenPromise;
      // authUser.token = token Account service (reducer dùng cho RequestApiPrivateUsers).
      const userInfo = { ...data, token };
      if (!data?.workspace || data?.workspace.status === 0) {
        callback && callback(undefined, userInfo);
        return userInfo;
      }

      localStorage.setItem("accessToken", token);
      callback && callback(undefined, userInfo);
      return userInfo;
    } catch (error: any) {
      toast.error(error?.response?.data?.message, { duration: 5000 });
      callback && callback(false);
      throw error;
    }
  }
);

export const setTokenGticket = createAsyncThunk(
  "auth/setTokenGticket",
  async (payload: { token: string }) => {
    const { token } = payload;
    try {
      RequestApiPrivateGticket.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
      RequestApiPrivateGTicketV2.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
      RequestApiPrivateQuickTicket.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
      RequestApiPrivateQuickTicketAdmin.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
      // report-GT của G-Ticket (report-gpms.ggg.com.vn) dùng chung token G-Ticket.
      axiosReportPrivate.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
      // Lưu ý: report Gương Thần (axiosGuongThanReportPrivate) dùng JWT report-GT
      // riêng (§1, generateReportToken) — xem magic-mirror/api/reportAuthApi.ts.
      // axiosAccountPrivateV2.defaults.headers.common[
      //   "Authorization"
      // ] = `Bearer ${token}`;
      // axiosAccountPrivateDomainV2.defaults.headers.common[
      //   "Authorization"
      // ] = `Bearer ${token}`;
      return { token };
    } catch (error) {
      throw error;
    }
  }
);

// §1.1 — Xác định user đang đăng nhập là Quản lý (`manager`) hay Nhân viên (`staff`)
// bằng cách gọi GET /api/reports/me (vai trò tính LIVE). Kết quả lưu vào redux để
// điều hướng menu/tab báo cáo Gương Thần. Cần JWT report-GT đã gắn (generateReportToken).
export const fetchReportRole = createAsyncThunk(
  "auth/fetchReportRole",
  async () => {
    const data = await getReportMe();
    return data.role;
  }
);

export const updateUserLocale = createAsyncThunk(
  "auth/updateUserLocale",
  async (payload: { userId: number; locale: string }) => {
    const { userId, locale } = payload;
    try {
      await AccountApi.updateLocale({ locale, userId });
      return locale;
    } catch (error) {
      renderServerErrorMsg(error, toast);
      throw error;
    }
  }
);

export async function loginUser(
  dispatch: any,
  user: any,
  callback?: any,
  callbackGetToken?: any
) {
  const payload = {
    accessToken: user.access_token,
    expiresAt: user.expires_at
  };
  dispatch(loginSSOSuccess(payload));
  // report-GT (Gương Thần): đổi SSO access token → JWT report-GT, gắn vào
  // axiosGuongThanReportPrivate (§1); sau đó lấy vai trò LIVE (§1.1) để xác định
  // Quản lý/Nhân viên. Không chặn login-flow chính nếu lỗi.
  generateReportToken(user.access_token)
    .then(() => {
      dispatch(fetchReportRole());
    })
    .catch((e) => {
      console.log("report-GT generate token error:\n", e);
    });
  if (user.profile && user.profile.email) {
    const userName: string = user.profile.username;
    const userEmail: string = user.profile.email;
    dispatch(
      fetchUserInfo({
        email: userEmail,
        phone: user?.profile?.phone,
        employeeid: user?.profile?.employeeid,
        username: userName,
        callback,
        dispatch,
        expiresAt: user.expires_at,
        ssoAccessToken: user.access_token
      })
    );
  }
}

export async function setTokenToApiPrivateUsers(token: string) {
  try {
    RequestApiPrivateUsers.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token}`;
  } catch (error) {
    throw error;
  }
}

const initialState: AuthState = {
  authUser: null,
  accessToken: null,
  expiresAt: null,
  status: "idle",
  updateLocaleStatus: "idle",
  kpiUser: { role_actions: {} },
  tokenGTicket: null,
  tokenGPerformance: null,
  isUserInactive: false,
  reportRole: null,
  reportRoleStatus: "idle"
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    loginSSOSuccess: (state, action) => {
      state.accessToken = action.payload.accessToken;
      state.expiresAt = action.payload.expiresAt;
    },
    logoutSuccess: (state) => {
      state.authUser = null;
      state.accessToken = null;
      state.expiresAt = null;
      state.reportRole = null;
      state.reportRoleStatus = "idle";
    },
    setTokenGPerformance: (state, action) => {
      state.tokenGPerformance = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.status = "succeeded";

        const userInfo = cloneDeep(action.payload);
        state.authUser = userInfo;
        if (!userInfo?.workspace || userInfo?.workspace.status === 0) {
          return;
        }
        if (!userInfo.language) userInfo.language = DEFAULT_LANGUAGE;
        if (userInfo.language.id === 1) userInfo.language = LANGUAGES.VN;
        if (userInfo.language.id === 2) userInfo.language = LANGUAGES.EN;

        if (userInfo.is_admin === 1) {
          userInfo.permissions = { ...userInfo.permissions, IS_ADMIN: 1 };
        } else {
          userInfo.permissions = { ...userInfo.permissions, IS_ADMIN: 0 };
        }
        updateI18nLocale(userInfo.language);
        setTokenToApiPrivateUsers(userInfo?.token ?? "");
        if (userInfo?.status === 0) {
          state.isUserInactive = true;
        }
      })
      .addCase(fetchUserInfo.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(updateUserLocale.pending, (state) => {
        state.updateLocaleStatus = "loading";
      })
      .addCase(updateUserLocale.fulfilled, (state, action) => {
        state.updateLocaleStatus = "succeeded";
        if (!state.authUser) return;
        let updateUser: UserType = { ...state.authUser };
        const updateLocale: any = action.payload;
        updateUser.language = updateLocale;
        state.authUser = updateUser;
        updateI18nLocale(updateLocale);
      })
      .addCase(updateUserLocale.rejected, (state) => {
        state.updateLocaleStatus = "failed";
      })
      .addCase(setTokenGticket.fulfilled, (state, action) => {
        state.tokenGTicket = action.payload.token;
      })
      .addCase(fetchReportRole.pending, (state) => {
        state.reportRoleStatus = "loading";
      })
      .addCase(fetchReportRole.fulfilled, (state, action) => {
        state.reportRoleStatus = "succeeded";
        state.reportRole = action.payload;
      })
      .addCase(fetchReportRole.rejected, (state) => {
        // §1.1: account lỗi/không xác định được → mặc định `staff` (an toàn nhất).
        state.reportRoleStatus = "failed";
        state.reportRole = "staff";
      });
  }
});

export const { loginSSOSuccess, logoutSuccess, setTokenGPerformance } =
  authSlice.actions;

export default authSlice.reducer;

export const selectAuthUser = (state: RootState) => state.auth.authUser;
export const tokenGperformance = (state: RootState) =>
  state.auth.tokenGPerformance;
export const expiresAt = (state: RootState) => state.auth.expiresAt;
export const selectIsUserInActive = (state: RootState) =>
  state.auth.isUserInactive;
/** Vai trò report-GT (Gương Thần) — `manager`/`staff`/null (chưa xác định). */
export const selectReportRole = (state: RootState) => state.auth.reportRole;
/** Trạng thái tải vai trò report-GT (§1.1) — dùng để gate UI trong lúc chờ. */
export const selectReportRoleStatus = (state: RootState) =>
  state.auth.reportRoleStatus;
